/**
 * Feature Flags for Beta 3 Roster Experience
 * 
 * TUNNEL_UI_DEV_PLAN_v2.md - Bucket 0 Deliverable
 * 
 * Production: TUNNEL=true, LEGACY=false
 * Debug/Support: Can enable LEGACY via env var only
 * 
 * @see legacy/LEGACY.md for legacy access documentation
 */

// =============================================================================
// FEATURE FLAGS
// =============================================================================

export const FEATURE_FLAGS = {
  /**
   * New Tunnel roster builder (default for all users)
   * 
   * Set VITE_FEATURE_ROSTER_TUNNEL=false to disable (not recommended)
   */
  ROSTER_TUNNEL: import.meta.env.VITE_FEATURE_ROSTER_TUNNEL !== 'false',
  
  /**
   * Legacy multi-slot builder (disabled by default)
   * 
   * ONLY enable for debugging/support:
   * Set VITE_FEATURE_LEGACY_BUILDER=true in .env.development
   * 
   * WARNING: Never enable in production
   */
  LEGACY_BUILDER: import.meta.env.VITE_FEATURE_LEGACY_BUILDER === 'true',
} as const;

// =============================================================================
// BUILDER SELECTION HOOK
// =============================================================================

export type BuilderType = 'tunnel' | 'legacy';

/**
 * Determines which roster builder to render.
 * 
 * Priority:
 * 1. If LEGACY_BUILDER explicitly enabled → 'legacy'
 * 2. If ROSTER_TUNNEL enabled (default) → 'tunnel'
 * 3. Fallback → 'tunnel'
 * 
 * @returns 'tunnel' | 'legacy'
 */
export const useRosterBuilder = (): BuilderType => {
  // Legacy takes precedence when explicitly enabled (debug mode)
  if (FEATURE_FLAGS.LEGACY_BUILDER) {
    console.warn('[FeatureFlags] Legacy builder enabled - for debugging only');
    return 'legacy';
  }
  
  // Default: Tunnel builder
  return 'tunnel';
};

// =============================================================================
// ENVIRONMENT VARIABLE DOCUMENTATION
// =============================================================================

/**
 * Environment Variables Reference:
 * 
 * Production (.env.production):
 *   VITE_FEATURE_ROSTER_TUNNEL=true
 *   VITE_FEATURE_LEGACY_BUILDER=false
 * 
 * Development (.env.development):
 *   VITE_FEATURE_ROSTER_TUNNEL=true
 *   VITE_FEATURE_LEGACY_BUILDER=false  # Set to true for debugging
 * 
 * Debug Mode (.env.local - gitignored):
 *   VITE_FEATURE_LEGACY_BUILDER=true
 */

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type FeatureFlags = typeof FEATURE_FLAGS;
